'use client';
import { useState, useEffect, useRef } from 'react';
import './UnifiedArchitecture.scss';

const STEPS = [
  {
    n: '01',
    color: 'var(--ua-cyan)',
    title: 'One agent, everywhere',
    body: 'A single lightweight agent deploys across your entire estate: no per-tool sprawl, no gaps.',
    check: 'Windows, Linux, and macOS endpoints, plus your cloud workloads, all running the same agent.',
  },
  {
    n: '02',
    color: 'var(--ua-violet)',
    title: 'One data plane, single ontology',
    body: 'Every signal streams into one place and lands in one shared language: assets, identities, processes, network, and events resolve to the same objects.',
    check: 'No stitching data across silos. Correlation is native, not an integration project.',
  },
  {
    n: '03',
    color: 'var(--ua-cyan)',
    title: 'One control plane',
    body: 'Every action travels back through the same governed path with policy and audit built in: isolate a host, patch a fleet, run a workflow.',
    check: 'Sense and respond in one closed loop: the same architecture that sees is the one that acts.',
  },
  {
    n: '04',
    color: 'var(--ua-green)',
    title: 'Three outcomes, one platform',
    body: 'Security, observability, and operations stop being separate products. They become three views of the same living system.',
    check: 'One agent. One data plane. One control plane. Everything else is an outcome.',
  },
];

const ENDPOINTS = [
  {
    label: 'Endpoints',
    sublabel: 'Windows · Linux · macOS',
    logos: [
      { src: '/img/logos/windows.png', alt: 'Windows' },
      { src: '/img/logos/apple.png', alt: 'macOS' },
      { src: '/img/logos/linux.png', alt: 'Linux' },
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="12" rx="2" />
        <path d="M8 20h8M12 16v4" />
      </svg>
    ),
  },
  {
    label: 'Cloud',
    sublabel: 'AWS · Azure · GCP',
    logos: [
      { src: '/img/logos/aws.png', alt: 'AWS' },
      { src: '/img/logos/azure.png', alt: 'Azure' },
      { src: '/img/logos/gcp.png', alt: 'Google Cloud' },
    ],
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7 18a4.5 4.5 0 1 1 .8-8.9A6 6 0 0 1 19.5 11 3.5 3.5 0 0 1 18 18H7Z" />
      </svg>
    ),
  },
  {
    label: 'Network',
    sublabel: 'Firewalls · Routers · DNS',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="6" r="2.3" />
        <circle cx="6" cy="17.5" r="2.3" />
        <circle cx="18" cy="17.5" r="2.3" />
        <path d="M10.9 8 7.1 15.5M13.1 8l3.8 7.5M8.3 17.5h7.4" />
      </svg>
    ),
  },
  {
    label: 'Identity',
    sublabel: 'SSO · Directory · MFA',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5.5 20c.8-3.4 3.4-5.5 6.5-5.5s5.7 2.1 6.5 5.5" />
      </svg>
    ),
  },
  {
    label: 'SaaS apps',
    sublabel: 'Email · CRM · Collaboration',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="4" width="6.5" height="6.5" rx="1.2" />
        <rect x="13.5" y="4" width="6.5" height="6.5" rx="1.2" />
        <rect x="4" y="13.5" width="6.5" height="6.5" rx="1.2" />
        <rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.2" />
      </svg>
    ),
  },
];

const OUTCOMES = [
  { label: 'Security', color: 'var(--ua-cyan)', title: 'Full-Stack Security', desc: 'Prevention, detection, response & compliance.', chips: ['XDR', 'Endpoint', 'SIEM', 'Cloud', 'Identity & Email', 'Compliance'] },
  { label: 'Observability', color: 'var(--ua-green)', title: 'Observability at Scale', desc: 'Metrics, logs & traces with security signal.', chips: ['Metrics', 'Logs', 'Traces', 'SLO'] },
  { label: 'Operations', color: 'var(--ua-violet)', title: 'Automated Ops', desc: 'Patch, configure & recover at machine speed.', chips: ['Patching', 'RMM', 'Runbooks', 'Ticketing'] },
];

export default function UnifiedArchitecture() {
  // The diagram is driven two ways: scroll (the active step tracks the text
  // that has reached the viewport midline) and click (jump to a step).
  const [active, setActive] = useState(0);
  const on = n => (active === n ? ' on' : '');
  const scrollyRef = useRef(null);
  const stepRefs = useRef([]);
  // While a click-driven scroll is settling, the scroll handler is suspended so
  // it doesn't recompute the active step from intermediate scroll positions.
  const lockUntil = useRef(0);

  useEffect(() => {
    const isMobile = () => window.matchMedia('(max-width: 900px)').matches;

    function onScroll() {
      if (isMobile()) return;
      if (Date.now() < lockUntil.current) return;
      const steps = stepRefs.current.filter(Boolean);
      const line = window.innerHeight * 0.5;
      const n = steps.reduce((acc, s, i) => {
        const title = s.querySelector('.ua-step-title') || s;
        return title.getBoundingClientRect().top <= line ? i : acc;
      }, 0);
      setActive(n);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const handleStepClick = (i, blockEl) => {
    setActive(i);
    const textEl = stepRefs.current[i];
    if (!textEl || !blockEl || window.matchMedia('(max-width: 900px)').matches) return;
    // Suspend scroll-spy while the smooth scroll runs so the click "wins".
    lockUntil.current = Date.now() + 900;
    // Scroll so the step text lines up vertically with the clicked block.
    const blockRect = blockEl.getBoundingClientRect();
    const textRect = textEl.getBoundingClientRect();
    const blockCenter = blockRect.top + blockRect.height / 2;
    const textCenter = textRect.top + textRect.height / 2;
    const target = window.scrollY + (textCenter - blockCenter);
    window.scrollTo({ top: target, behavior: 'smooth' });
  };

  // Make a diagram block clickable to activate its matching step.
  const pick = n => ({
    onClick: e => handleStepClick(n, e.currentTarget),
    role: 'button',
    tabIndex: 0,
    onKeyDown: e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleStepClick(n, e.currentTarget);
      }
    },
  });

  return (
    <section className="ua-section">
      {/* ── Section header ── */}
      <div className="ua-intro">
        <p className="ua-eyebrow"><span className="ua-dot" />One Unified Architecture</p>
        <h2 className="ua-heading">
          One AI-native<br />
          architecture.<br />
          <span className="ua-hl">Every outcome.</span>
        </h2>
        <p className="ua-sub">
          One agent on every system. One data plane with a single ontology. One control plane for every action.
        </p>
      </div>

      <div className="ua-scrolly" ref={scrollyRef}>
        <div className="ua-cols">

          {/* LEFT: step labels (driven by scroll + diagram clicks) */}
          <div className="ua-steps">
            {STEPS.map((s, i) => (
              <div
                key={i}
                ref={el => { stepRefs.current[i] = el; }}
                className={`ua-step${active === i ? ' active' : ''}`}
                style={{ '--step-color': s.color }}
              >
                <h3 className="ua-step-title">{s.title}</h3>
                <span className="ua-step-body">
                  <span className="ua-dot ua-dot--inline" style={{ background: s.color }} />
                  {s.body}
                </span>
              </div>
            ))}
          </div>

          {/* RIGHT: cohesive diagram box */}
          <div className="ua-stage-col">
            <div className="ua-stage">
              <div className="ua-diagram">

                <p className={`ua-ep-cap${on(0)}`} data-at="0">One agent · every system</p>

                {/* Endpoints */}
                <div className={`ua-endpoints${on(0)}`} data-at="0" {...pick(0)}>
                  {ENDPOINTS.map((ep, i) => (
                    <div key={i} className="ua-ep">
                      <span className="ua-ep-icon">{ep.icon}</span>
                      <b>{ep.label}</b>
                    </div>
                  ))}
                </div>

                {/* Streams */}
                <div className={`ua-streams${on(1)}`} data-at="1" aria-hidden="true">
                  <svg viewBox="0 0 640 42" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="ua-sg" x1="0" y1="0" x2="0" y2="42" gradientUnits="userSpaceOnUse">
                        <stop offset="0" stopColor="#a78bfa" stopOpacity=".25" />
                        <stop offset="1" stopColor="#a78bfa" stopOpacity=".6" />
                      </linearGradient>
                    </defs>
                    <path id="ua-st1" d="M64,0 C64,26 320,17.5 320,42" fill="none" stroke="url(#ua-sg)" strokeWidth="1" />
                    <path id="ua-st2" d="M192,0 C192,26 320,19 320,42" fill="none" stroke="url(#ua-sg)" strokeWidth="1" />
                    <path id="ua-st3" d="M320,0 C320,14 320,28 320,42" fill="none" stroke="url(#ua-sg)" strokeWidth="1" />
                    <path id="ua-st4" d="M448,0 C448,26 320,19 320,42" fill="none" stroke="url(#ua-sg)" strokeWidth="1" />
                    <path id="ua-st5" d="M576,0 C576,26 320,17.5 320,42" fill="none" stroke="url(#ua-sg)" strokeWidth="1" />
                  </svg>
                </div>

                {/* Data plane */}
                <div className={`ua-plane ua-plane--dp${on(1)}`} data-at="1" {...pick(1)}>
                  <div className="ua-pl-head">
                    <span className="ua-pl-badge">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#a78bfa" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <ellipse cx="12" cy="5.5" rx="8" ry="3" />
                        <path d="M4 5.5v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
                        <path d="M4 11.5v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
                      </svg>
                    </span>
                    <span className="ua-pl-title">
                      <b>One Data Plane</b>
                      <span>All telemetry, one store, queryable in real time</span>
                    </span>
                    <span className="ua-pl-chips">
                      {['logs', 'metrics', 'traces', 'events'].map(c => (
                        <span key={c} className="ua-chip ua-chip--dp">{c}</span>
                      ))}
                    </span>
                  </div>
                  <div className="ua-ontology">
                    <span className="ua-ont-label">Single ontology</span>
                  </div>
                </div>

                {/* Stem */}
                <div className={`ua-stem${on(2)}`} data-at="2" aria-hidden="true">
                  <svg viewBox="0 0 8 44" preserveAspectRatio="none" style={{ width: '8px' }}>
                    <path id="ua-cstem" d="M4,0 L4,44" stroke="rgba(76,201,240,.45)" strokeWidth="1.5" fill="none" />
                  </svg>
                </div>

                {/* Control plane */}
                <div className={`ua-plane ua-plane--cp${on(2)}`} data-at="2" {...pick(2)}>
                  <div className="ua-pl-head">
                    <span className="ua-pl-badge">
                      <svg viewBox="0 0 24 24" fill="none" stroke="#4cc9f0" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 7h10M4 12h16M4 17h7" />
                        <circle cx="17" cy="7" r="2.4" />
                        <circle cx="14" cy="17" r="2.4" />
                      </svg>
                    </span>
                    <span className="ua-pl-title">
                      <b>One Control Plane</b>
                      <span>Every action through one governed, audited path</span>
                    </span>
                    <span className="ua-pl-chips">
                      {['isolate', 'patch', 'query', 'automate'].map(c => (
                        <span key={c} className="ua-chip ua-chip--cp">{c}</span>
                      ))}
                    </span>
                  </div>
                </div>

                {/* Stem to outcomes */}
                <div className={`ua-stem${on(3)}`} data-at="3" aria-hidden="true">
                  <svg viewBox="0 0 8 44" preserveAspectRatio="none" style={{ width: '8px' }}>
                    <path id="ua-ostem" d="M4,0 L4,44" stroke="rgba(74,222,128,.4)" strokeWidth="1.5" fill="none" />
                  </svg>
                </div>

                {/* Outcomes */}
                <div className="ua-outcomes" {...pick(3)}>
                  {OUTCOMES.map((oc, i) => (
                    <div key={i} className={`ua-oc${on(3)}`} style={{ '--oc': oc.color, '--d': `${0.05 + i * 0.15}s` }} data-at="3">
                      <p className="ua-oc-eyebrow"><i />{oc.label}</p>
                      <b>{oc.title}</b>
                      <span>{oc.desc}</span>
                      {oc.chips && (
                        <div className="ua-oc-chips">
                          {oc.chips.map(c => (
                            <span key={c} className="ua-oc-chip">{c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                <p className={`ua-foundation-cap${on(3)}`} data-at="3">One platform · every outcome</p>

              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
